import { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  FiAlertCircle,
  FiBell,
  FiCalendar,
  FiClock,
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiPhone,
  FiPlus,
  FiSave,
  FiTrash2,
  FiUser
} from "react-icons/fi";
import "../../../assets/css/doctor/doctorpages/DoctorSettings.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import { AppContext } from "../../../context/AppContext";

const Settings = () => {
  const { baseUrl, logout } = useContext(AppContext);
  const token = localStorage.getItem("therapist_token");
  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  };

  const [activeTab, setActiveTab] = useState("account");

  // ── Account ──────────────────────────────────────────────
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [accountLoading, setAccountLoading] = useState(true);
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountMessage, setAccountMessage] = useState(null);

  // ── Security ──────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [securitySaving, setSecuritySaving] = useState(false);
  const [securityMessage, setSecurityMessage] = useState(null);

  // ── Notifications ─────────────────────────────────────────
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifMessage, setNotifMessage] = useState(null);

  // ── Availability Builder ──────────────────────────────────
  const [selectedDay, setSelectedDay] = useState("Monday");
  const [slotStart, setSlotStart] = useState("09:00");
  const [slotEnd, setSlotEnd] = useState("17:00");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [slotSaving, setSlotSaving] = useState(false);
  const [slotsMessage, setSlotsMessage] = useState(null);

  const weekDays = [
    "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
  ];

  // ── Fetch: Account (profile) ───────────────────────────────
  useEffect(() => {
    if (!token) {
      setAccountLoading(false);
      return;
    }
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${baseUrl}/doctor/TherapistProfile/show`, authHeaders);
        const data = res.data?.data ?? res.data ?? {};
        setFullName(data.full_name || data.name || "");
        setEmail(data.email || "");
        setPhone(data.phone || data.phone_number || "");
      } catch (err) {
        console.log("Profile fetch error:", err);
        if (err?.response?.status === 401) logout();
      } finally {
        setAccountLoading(false);
      }
    };
    fetchProfile();
  }, [baseUrl]);

  // ── Fetch: Notification preferences ────────────────────────
  useEffect(() => {
    if (!token) {
      setNotifLoading(false);
      return;
    }
    const fetchPrefs = async () => {
      try {
        const res = await axios.get(`${baseUrl}/notification-preferences`, authHeaders);
        const data = res.data?.data ?? res.data ?? {};
        setEmailNotifications(data.email_notifications ?? true);
        setInAppNotifications(data.in_app_notifications ?? true);
      } catch (err) {
        console.log("Notification prefs fetch error:", err);
        if (err?.response?.status === 401) logout();
      } finally {
        setNotifLoading(false);
      }
    };
    fetchPrefs();
  }, [baseUrl]);

  // ── Fetch: Availability slots ───────────────────────────────
  useEffect(() => {
    if (!token) {
      setSlotsLoading(false);
      return;
    }
    const fetchSlots = async () => {
      try {
        const res = await axios.get(`${baseUrl}/doctor/Availability/list`, authHeaders);
        const data = res.data?.data ?? res.data ?? [];
        const normalized = (Array.isArray(data) ? data : []).map((s) => ({
          id: s.id,
          day: s.day || s.week_day,
          startTime: s.start_time || s.startTime,
          endTime: s.end_time || s.endTime,
        }));
        setSlots(normalized);
      } catch (err) {
        console.log("Availability fetch error:", err);
        if (err?.response?.status === 401) logout();
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchSlots();
  }, [baseUrl]);

  // ── Handlers ──────────────────────────────────────────────
  const handleSaveAccount = async () => {
    setAccountSaving(true);
    setAccountMessage(null);
    try {
      await axios.post(
        `${baseUrl}/doctor/TherapistProfile/save`,
        { full_name: fullName, email, phone },
        authHeaders
      );
      setAccountMessage({ type: "success", text: "Account settings saved successfully!" });
    } catch (err) {
      console.log("Save account error:", err);
      if (err?.response?.status === 401) {
        logout();
      } else {
        setAccountMessage({ type: "error", text: "Could not save account settings." });
      }
    } finally {
      setAccountSaving(false);
    }
  };

  const handleSaveSecurity = async () => {
    if (newPassword !== confirmPassword) {
      setSecurityMessage({ type: "error", text: "New passwords do not match!" });
      return;
    }
    setSecuritySaving(true);
    setSecurityMessage(null);
    try {
      await axios.post(
        `${baseUrl}/doctor/DoctorAuth/changePassword`,
        {
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirmation: confirmPassword,
        },
        authHeaders
      );
      setSecurityMessage({ type: "success", text: "Security settings updated successfully!" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.log("Change password error:", err);
      if (err?.response?.status === 401) {
        logout();
      } else {
        setSecurityMessage({
          type: "error",
          text: err?.response?.data?.message || "Could not update password.",
        });
      }
    } finally {
      setSecuritySaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setNotifSaving(true);
    setNotifMessage(null);
    try {
      await axios.put(
        `${baseUrl}/notification-preferences`,
        {
          email_notifications: emailNotifications,
          in_app_notifications: inAppNotifications,
        },
        authHeaders
      );
      setNotifMessage({ type: "success", text: "Notification preferences saved!" });
    } catch (err) {
      console.log("Save notifications error:", err);
      if (err?.response?.status === 401) {
        logout();
      } else {
        setNotifMessage({ type: "error", text: "Could not save preferences." });
      }
    } finally {
      setNotifSaving(false);
    }
  };

  const generateSlots = async () => {
    if (!slotStart || !slotEnd || slotStart >= slotEnd) return;
    setSlotSaving(true);
    setSlotsMessage(null);
    try {
      const res = await axios.post(
        `${baseUrl}/doctor/Availability/add`,
        { day: selectedDay, start_time: slotStart, end_time: slotEnd },
        authHeaders
      );
      const created = res.data?.data ?? res.data ?? {};
      setSlots((prev) => [
        ...prev,
        {
          id: created.id || Date.now(),
          day: selectedDay,
          startTime: slotStart,
          endTime: slotEnd,
        },
      ]);
    } catch (err) {
      console.log("Add slot error:", err);
      if (err?.response?.status === 401) {
        logout();
      } else {
        setSlotsMessage({ type: "error", text: "Could not add this slot." });
      }
    } finally {
      setSlotSaving(false);
    }
  };

  const deleteSlot = async (id) => {
    const previous = slots;
    setSlots((prev) => prev.filter((s) => s.id !== id));
    try {
      await axios.delete(`${baseUrl}/doctor/Availability/${id}`, authHeaders);
    } catch (err) {
      console.log("Delete slot error:", err);
      setSlots(previous); // rollback on failure
      if (err?.response?.status === 401) {
        logout();
      } else {
        setSlotsMessage({ type: "error", text: "Could not delete this slot." });
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
      return;
    }
    try {
      await axios.post(`${baseUrl}/doctor/DoctorAuth/deleteAccount`, {}, authHeaders);
      alert("Account deletion process initiated. You will receive a confirmation email.");
      logout();
    } catch (err) {
      console.log("Delete account error:", err);
      alert(err?.response?.data?.message || "Could not delete account.");
    }
  };

  const tabs = [
    { id: "account",       label: "Account",       icon: <FiUser /> },
    { id: "security",      label: "Security",       icon: <FiLock /> },
    { id: "notifications", label: "Notifications",  icon: <FiBell /> },
    { id: "availability",  label: "Availability",   icon: <FiCalendar /> },
  ];

  return (
    <DoctorMasterLayout>
      <div className="ds-settings-container">
        <div className="ds-settings-content">

          {/* Header */}
          <div className="ds-settings-header">
            <h2>Settings</h2>
            <p>Manage your account preferences and configurations</p>
          </div>

          {/* Tabs */}
          <div className="ds-settings-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`ds-tab-btn ${activeTab === tab.id ? "ds-tab-active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="ds-tab-content">

            {/* ── Account ── */}
            {activeTab === "account" && (
              <div className="ds-settings-section">
                <div className="ds-section-header">
                  <h3>Account Information</h3>
                  <p>Update your personal account details</p>
                </div>

                <div className="ds-form-content">
                  {accountLoading ? (
                    <p className="ds-loading">Loading account details…</p>
                  ) : (
                    <>
                      <div className="ds-form-group">
                        <label htmlFor="fullName">
                          <FiUser /> Full Name
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                        />
                      </div>

                      <div className="ds-form-row">
                        <div className="ds-form-group">
                          <label htmlFor="email">
                            <FiMail /> Email Address
                          </label>
                          <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>

                        <div className="ds-form-group">
                          <label htmlFor="phone">
                            <FiPhone /> Phone Number
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                          />
                        </div>
                      </div>

                      {accountMessage && (
                        <p className={`ds-message ds-message-${accountMessage.type}`}>
                          {accountMessage.text}
                        </p>
                      )}

                      <div className="ds-action-buttons">
                        <button className="ds-btn-save" onClick={handleSaveAccount} disabled={accountSaving}>
                          <FiSave /> {accountSaving ? "Saving…" : "Save Changes"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ── Security ── */}
            {activeTab === "security" && (
              <div className="ds-settings-section">
                <div className="ds-section-header">
                  <h3>Security Settings</h3>
                  <p>Manage your password and security options</p>
                </div>

                <div className="ds-form-content">
                  <div className="ds-security-card">
                    <h4><FiLock /> Change Password</h4>

                    <div className="ds-form-group">
                      <label htmlFor="currentPassword">Current Password</label>
                      <div className="ds-password-input">
                        <input
                          type={showCurrent ? "text" : "password"}
                          id="currentPassword"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Enter current password"
                        />
                        <button type="button" onClick={() => setShowCurrent(!showCurrent)}>
                          {showCurrent ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                    </div>

                    <div className="ds-form-row">
                      <div className="ds-form-group">
                        <label htmlFor="newPassword">New Password</label>
                        <div className="ds-password-input">
                          <input
                            type={showNew ? "text" : "password"}
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                          />
                          <button type="button" onClick={() => setShowNew(!showNew)}>
                            {showNew ? <FiEyeOff /> : <FiEye />}
                          </button>
                        </div>
                      </div>

                      <div className="ds-form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <div className="ds-password-input">
                          <input
                            type={showConfirm ? "text" : "password"}
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                          />
                          <button type="button" onClick={() => setShowConfirm(!showConfirm)}>
                            {showConfirm ? <FiEyeOff /> : <FiEye />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {securityMessage && (
                      <p className={`ds-message ds-message-${securityMessage.type}`}>
                        {securityMessage.text}
                      </p>
                    )}

                    <button className="ds-btn-save" onClick={handleSaveSecurity} disabled={securitySaving}>
                      <FiSave /> {securitySaving ? "Updating…" : "Update Password"}
                    </button>
                  </div>

                  <div className="ds-danger-zone">
                    <h4><FiAlertCircle /> Danger Zone</h4>
                    <p>Once you delete your account, there is no going back. Please be certain.</p>
                    <button className="ds-btn-delete" onClick={handleDeleteAccount}>
                      <FiTrash2 /> Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Notifications ── */}
            {activeTab === "notifications" && (
              <div className="ds-settings-section">
                <div className="ds-section-header">
                  <h3>Notification Preferences</h3>
                  <p>Choose how you want to be notified</p>
                </div>

                <div className="ds-form-content">
                  {notifLoading ? (
                    <p className="ds-loading">Loading preferences…</p>
                  ) : (
                    <>
                      <div className="ds-notification-group">
                        <h4><FiBell /> General Notifications</h4>

                        <div className="ds-toggle-setting">
                          <div className="ds-toggle-info">
                            <strong>Email Notifications</strong>
                            <p>Receive notifications via email</p>
                          </div>
                          <label className="ds-toggle-switch">
                            <input
                              type="checkbox"
                              checked={emailNotifications}
                              onChange={() => setEmailNotifications(!emailNotifications)}
                            />
                            <span className="ds-slider"></span>
                          </label>
                        </div>

                        <div className="ds-toggle-setting">
                          <div className="ds-toggle-info">
                            <strong>In-App Notifications</strong>
                            <p>Receive notifications inside the application</p>
                          </div>
                          <label className="ds-toggle-switch">
                            <input
                              type="checkbox"
                              checked={inAppNotifications}
                              onChange={() => setInAppNotifications(!inAppNotifications)}
                            />
                            <span className="ds-slider"></span>
                          </label>
                        </div>
                      </div>

                      {notifMessage && (
                        <p className={`ds-message ds-message-${notifMessage.type}`}>
                          {notifMessage.text}
                        </p>
                      )}

                      <div className="ds-action-buttons">
                        <button className="ds-btn-save" onClick={handleSaveNotifications} disabled={notifSaving}>
                          <FiSave /> {notifSaving ? "Saving…" : "Save Preferences"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ── Availability ── */}
            {activeTab === "availability" && (
              <div className="ds-settings-section">
                <div className="ds-section-header">
                  <h3>Working Hours</h3>
                  <p>Set your weekly availability schedule</p>
                </div>

                <div className="ds-form-content">
                  <div className="ds-availability-builder">
                    <h4><FiClock /> Add Availability Slot</h4>

                    <div className="ds-avail-row">
                      <div className="ds-avail-field">
                        <label>Week Day</label>
                        <select
                          value={selectedDay}
                          onChange={(e) => setSelectedDay(e.target.value)}
                        >
                          {weekDays.map((d) => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>

                      <div className="ds-avail-field">
                        <label>Start Time</label>
                        <input
                          type="time"
                          value={slotStart}
                          onChange={(e) => setSlotStart(e.target.value)}
                        />
                      </div>

                      <div className="ds-avail-field">
                        <label>End Time</label>
                        <input
                          type="time"
                          value={slotEnd}
                          onChange={(e) => setSlotEnd(e.target.value)}
                        />
                      </div>

                      <button className="ds-btn-generate" onClick={generateSlots} disabled={slotSaving}>
                        <FiPlus /> {slotSaving ? "Adding…" : "Generate"}
                      </button>
                    </div>
                  </div>

                  {slotsMessage && (
                    <p className="ds-message ds-message-error">{slotsMessage.text}</p>
                  )}

                  {/* Slots Table */}
                  <div className="ds-slots-table-wrapper">
                    <h4>Generated Slots</h4>
                    <div className="ds-slots-table-scroll">
                      <table className="ds-slots-table">
                        <thead>
                          <tr>
                            <th>Day</th>
                            <th>Start Time</th>
                            <th>End Time</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {slotsLoading ? (
                            <tr>
                              <td colSpan={4} className="ds-no-slots">Loading slots…</td>
                            </tr>
                          ) : slots.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="ds-no-slots">
                                No slots generated yet. Add one above.
                              </td>
                            </tr>
                          ) : (
                            slots.map((slot) => (
                              <tr key={slot.id}>
                                <td>{slot.day}</td>
                                <td>{slot.startTime}</td>
                                <td>{slot.endTime}</td>
                                <td>
                                  <button
                                    className="ds-btn-delete-slot"
                                    onClick={() => deleteSlot(slot.id)}
                                  >
                                    <FiTrash2 /> Delete
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </DoctorMasterLayout>
  );
};

export default Settings;